import { EmploymentType } from "@/enum/job.enum";
import { NewInRoleChangeType } from "@/enum/company-enrich-type.enum";
import { ParallelAiMonitorFrequency } from "@/enum/parallel-ai-type.enum";
import { FundingCategories, FundingCountries, FundingRoundsValues } from "@/enum/funding-ma.enum";
import { Industry } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

import { IcpPosition } from "@/pages/Private/pages/List/pages/ListEditDetails/enum/icp.enum";

import { ListRecommendations } from "../../schema/icp";
import {
	JobLocation,
	OnboardingListConfig,
	OnboardingListTypeKey,
	PeopleAtCompanyConfig,
} from "./onboardingListTypes";

/** Default rolling window of the topic list, in days. */
export const TOPIC_TIME_FRAME_DAYS = 30;
/** Default lookback of the funding / M&A lists, in days. */
export const FUNDING_LAST_X_DAYS = 90;
/** People enriched per matching company, as the in-app details step defaults it. */
export const DEFAULT_MAX_PER_COMPANY = 3;

/** Everything the picker prefills a fresh config from. */
export interface ListPrefill {
	recommendations: ListRecommendations | null;
	/** The ICP location resolved to the `{ id, name }` the JOBS source needs. */
	icpLocation: JobLocation | null;
	/** The recommended industry names, resolved against the industry lookup. */
	industries: Industry[];
}

const EMPTY_PREFILL: ListPrefill = {
	recommendations: null,
	icpLocation: null,
	industries: [],
};

/**
 * Starts the "who to find at each company" profile from the recommendation, on
 * the most specific option it can actually fill: a recommended job title beats
 * a department/seniority pair, and with neither the search falls back to the
 * company's leadership so the profile is never left blocking and empty.
 */
const defaultPeopleAtCompany = (
	recommendations: ListRecommendations | null
): PeopleAtCompanyConfig => {
	const [title] = recommendations?.jobTitles ?? [];
	const seniority = recommendations?.seniority ?? [];
	const department = recommendations?.departments ?? [];

	const position = title
		? IcpPosition.CUSTOM
		: seniority.length > 0 || department.length > 0
			? IcpPosition.DEPARTMENT_SENIORITY
			: IcpPosition.CEO_AND_SIMILAR;

	return {
		position,
		title: title ?? "",
		seniority,
		department,
		maxPerCompany: DEFAULT_MAX_PER_COMPANY,
	};
};

/** Keeps the recommended values a fixed-option field actually offers. */
const offeredOnly = (values: string[], options: { id: string }[]): string[] =>
	values.filter((value) => options.some((option) => option.id === value));

/**
 * Builds the config a newly selected list type starts with. Every field the
 * recommendation can speak to is prefilled, so the user confirms a targeted
 * list instead of filling one in from scratch.
 */
export const defaultConfigFor = (
	key: OnboardingListTypeKey,
	prefill: ListPrefill = EMPTY_PREFILL
): OnboardingListConfig => {
	const { recommendations: rec, icpLocation, industries } = prefill;
	const positionQuery = rec?.jobTitles ?? [];
	const seniority = rec?.seniority ?? [];
	const department = rec?.departments ?? [];
	const countries = rec?.countries ?? [];
	const employees = rec?.employeeSizes ?? [];
	const peopleAtCompany = defaultPeopleAtCompany(rec);

	switch (key) {
		case "hiring":
			return {
				kind: "hiring",
				query: rec?.hiringQuery ?? "",
				location: icpLocation ?? null,
				employmentType: EmploymentType.FULLTIME,
				positionTitles: positionQuery,
				peopleAtCompany,
			};

		case "lookalike":
			return {
				kind: "lookalike",
				domains: rec?.lookalikeDomains ?? [],
				semanticQuery: "",
				companyCountries: countries,
				employees,
				positionQuery,
				seniority,
				countries,
			};

		case "leadDb":
			return {
				kind: "leadDb",
				semanticQuery: "",
				positionQuery,
				seniority,
				department,
				countries,
				industries,
				employees,
				companyCountries: countries,
				// Deliberately NOT prefilled from the recommendation (PREC-3861): the
				// model's keywords are matched literally against the lead database and
				// routinely narrowed a well-targeted search down to nothing. The field
				// stays, so a user who knows the vocabulary can still add their own.
				keywords: [],
			};

		case "newInRole":
			return {
				kind: "newInRole",
				changeTypes: [NewInRoleChangeType.NEW_ROLE, NewInRoleChangeType.NEW_EMPLOYER],
				windowPreset: "90",
				department,
				seniority,
				countries,
				industries,
				employees,
			};

		case "topic":
			return {
				kind: "topic",
				query: rec?.topic ?? "",
				authorJobTitle: "",
				timeFrame: TOPIC_TIME_FRAME_DAYS,
				capture: { posts: true, likes: true, comments: true },
			};

		case "gmaps":
			return {
				kind: "gmaps",
				query: rec?.businessType ?? "",
				country: "",
				city: "",
				peopleAtCompany,
			};

		case "aiCompany":
			return { kind: "aiCompany", query: rec?.companyDescription ?? "", peopleAtCompany };

		case "aiNews":
			return {
				kind: "aiNews",
				query: rec?.newsQuery ?? "",
				frequency: ParallelAiMonitorFrequency.DAILY,
				peopleAtCompany,
			};

		case "funding":
			return {
				kind: "funding",
				countries: offeredOnly(countries, FundingCountries),
				category: offeredOnly(rec?.fundingCategories ?? [], FundingCategories),
				round: offeredOnly(rec?.fundingRounds ?? [], FundingRoundsValues),
				lastXDays: FUNDING_LAST_X_DAYS,
				peopleAtCompany,
			};

		case "mna":
			return {
				kind: "mna",
				countries: offeredOnly(countries, FundingCountries),
				category: offeredOnly(rec?.fundingCategories ?? [], FundingCategories),
				lastXDays: FUNDING_LAST_X_DAYS,
				peopleAtCompany,
			};
	}
};
